import Vue from "vue";
import VueRouter from "vue-router";
import { store } from "./store/store";
import { getApiUrl, getSwagger } from "sinao-corejs";
import * as Sentry from "@sentry/browser";
import { getUrlParams } from "./mixins/methods";
import i18n from "./i18n";
import { mdToHtml } from "./mixins/methods";

Vue.use(VueRouter);

const routes = [
	{
		path: "/",
		alias: "/index.html",
		component: () => import("./App.vue"),
		beforeEnter: (to, from, next) => {
			// if (!["fr", "en", "es", "it"].includes(to.query.lang)) {
			if (!["fr", "en"].includes(to.query.lang)) {
				to.query.lang = "fr";
			}

			const lang = to.query.lang || "fr";
			i18n.locale = lang;
			next();
		},
	}
];

const router = new VueRouter({
	mode: "history",
	base: process.env.BASE_URL,
	routes
});

router.beforeEach(async (to, from, next) => {
	let { appId, documentId, type, onboarding, recurring, sign, force, admin, removePreview, api_key, eventContact } = getUrlParams(window.location.search);

	appId = appId ? Number(appId) : undefined;
	documentId = documentId ? Number(documentId) : undefined;
	recurring = recurring && recurring === "true" ? true : false;
	onboarding = onboarding && onboarding === "true" ? true : false;
	sign = sign && sign === "true" ? true : false;
	force = force && force === "true" ? true : false;
	admin = admin && admin === "true" ? true : false;
	removePreview = removePreview && removePreview === "true" ? true : false;
	eventContact = eventContact && eventContact === "true" ? true : false;

	if (typeof appId === "undefined") {
		throw new Error("No appId");
	}

	if (typeof type === "undefined" || !["invoices", "quotes"].includes(type)) {
		throw new Error("No type (invoices or quotes)");
	}

	store.commit("admin", admin);
	store.commit("id", appId);
	store.commit("pageType", type);
	store.commit("removePreview", removePreview);
	store.commit("eventContact", eventContact);

	await getSwagger();

	// API Url
	const apiUrl = await getApiUrl();

	// apiUrl
	store.commit("apiUrl", apiUrl);
	store.commit("apiKey", api_key);

	if (!sign) {
		if (appId === 0) {
			store.commit("loading", false);
		} else {
			if (documentId) {
				store.commit("documentId", documentId);
			}

			if (recurring) {
				store.commit("isRecurringInvoice", true);
				//store.dispatch("getPeriods");
			} else {
				store.commit("isRecurringInvoice", false);
			}

			// Load currencies
			const currencies = store.dispatch("getCurrencies");

			// Load document printable langs
			const langs = store.dispatch("getPrintableLangs");

			// Load app
			const app = store.dispatch("app", appId);

			// Load config
			const config = store.dispatch("config", appId);

			// Load models
			const models = store.dispatch("models", appId);

			// Loads accounts
			const accounts = store.dispatch("accounts", appId);

			// Load document
			const loadDocument = documentId && !recurring ? store.dispatch("loadDocument", { data: null, force }) : {};

			// Load app
			const lastDocument = store.dispatch("lastDocument");

			// Load relationships
			store.dispatch("relationships");

			// Load RIBs
			store.dispatch("bankdetails", appId);

			// Load products is now done in Entry component


			// Load invoices
			if (type === "invoices" && !recurring) {
				store.dispatch("invoices", { appId });
			}

			// eslint-disable-next-line compat/compat
			Promise.all([config, app, models, accounts, loadDocument, lastDocument, currencies, langs]).then(response => { // langs ,
				const config = response[0];
				const app = response[1];
				const loadDocument = response[4];
				const currencies = response[6];
				const langs = response[7];

				Sentry.setUser({
					id: app.id,
					username: app.identifiant,
					ip_address: "{{auto}}"
				});

				Sentry.setContext("API", {
					apiUrl,
					app,
					organization: app.organization
				});

				// config
				if (!documentId) {
					const mention_from_config = config?.accounting?.mention?.[type] ?? "";
					if (mention_from_config.trim() !== "") {
						store.commit("mention", mdToHtml(mention_from_config));
					} else {
						store.commit("mention", "<p><br></p>");
					}
				}

				if (config?.accounting?.accounts["default-sales"]) {
					store.commit("technicalName", config?.accounting?.accounts["default-sales"]);
				}
				
				if (config?.currency)
					store.dispatch("initCurrencyByIso", { isoCode: config.currency, from: "router" }); 

				if (config?.accounting?.printablelogo) {
					store.commit("logoURL", `${config?.accounting?.printablelogo}?ts=${Date.now()}`);
				}

				if (config?.accounting?.vat?.name) {
					store.commit("vatName", config.accounting.vat.name);
				}

				if (config?.accounting?.vat?.vat_regime && !store.state.home.vat_regime) {
					store.commit("vat_regime", config.accounting.vat.vat_regime);
				}

				if (config?.accounting?.delay) {
					config.accounting.delay.translations['fr'] = { name: "Français", compensation: config.accounting.delay.compensation, rate: config.accounting.delay.rate, payment: config.accounting.delay.payment };

					store.commit("vatValues", config?.accounting?.vat?.values ?? []);
					store.commit("selectedVatValue", config?.accounting?.vat?.defaultvalue ?? 20);

					if (config.accounting.delay.rate?.std) {
						// store.commit("penalityPercentage", config?.accounting?.delay?.rate?.std);
						store.commit("penalityPercentage", config?.accounting?.delay?.translations[i18n.locale].rate?.std);
					}
					if (config.accounting.delay.rate) {
						// store.commit("penalityTextTemplate", config?.accounting?.delay?.rate.text);
						store.commit("penalityTextTemplate", config?.accounting?.delay?.translations[i18n.locale].rate.text);
					}

					if (config.accounting.delay.compensation?.std) {
						// store.commit("penalityAmount", config?.accounting?.delay?.compensation?.std);
						store.commit("penalityAmount", config?.accounting?.delay?.translations[i18n.locale].compensation?.std);
					}
					if (config.accounting.delay.compensation) {
						// store.commit("indemnityTextTemplate", config?.accounting?.delay?.compensation.text);
						store.commit("indemnityTextTemplate", config?.accounting?.delay?.translations[i18n.locale].compensation.text);
					}

					if (config.accounting.delay.payment) {
						// store.commit("paymentTextDaysTemplate", config.accounting.delay.payment["text-days"]);
						// store.commit("paymentTextNowTemplate", config.accounting.delay.payment["text-now"]);
						store.commit("paymentTextDaysTemplate", config.accounting.delay.translations[i18n.locale].payment["text-days"]);
						store.commit("paymentTextNowTemplate", config.accounting.delay.translations[i18n.locale].payment["text-now"]);
					}
				}

				if (config?.template) {
					if (config.template["color-primary"]) {
						store.commit("color", {
							slot: "background",
							color: config?.template?.["color-primary"]
						});
					}

					if (config.template["color-second-text"]) {
						store.commit("color", {
							slot: "foreground",
							color: config?.template?.["color-second-text"]
						});
					}
				}

				if (onboarding) {
					store.commit("onboarding", true);
					store.commit("showModalDocumentStyle", true);
				}

				try {
					if (!config?.installation?.["profile-onboarding"]) {
						const modals = [
							"showModalDocumentStyle",
							"showPopoverLogo",
							"showPopoverNumbering",
							"showPopoverCompany",
							"showPopoverPayment",
							"showPopoverBankDetails",
							"showPopoverEndOnboarding"
						];
						// no key already exists in app settings
						if (!config?.installation?.onboarding) {
							store.commit("onboarding", true);
							for (let modal of modals) {
								store.commit("setPopovers", { key: modal, value: true});
								store.commit(modal, true);
							}
						}
						// some key exists, but some ar not, we need to open fragments
						else if (Object.values(config?.installation?.onboarding).some(value => value === false)) {
							store.commit("onboarding", true);
							let first_popover_to_show = null;
							for (let modal of modals) {
								if (first_popover_to_show === null && !config?.installation?.onboarding[modal])
									first_popover_to_show = modal;
								store.commit("setPopovers", { key: modal, value: !config?.installation?.onboarding[modal]});
								store.commit(modal, false);
							}
							// set the first fragment to true
							store.commit(first_popover_to_show, true);

						}
						// set total_fragments_to_show
						store.dispatch("computeTotalPopoversToShow");

					}
				} catch (error) {
					console.error(error);
				}

				if (!documentId || (documentId && loadDocument)) {
					// loading
					store.commit("loading", false);
				}
			});
		}
	}

	next();
});

export default router;
